import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import type { InvoiceInstallment } from '../../../../../Data/invoice.dto';
import { formatINR } from '../../../../../Utils/helper';
import BrandDatePicker from '../../../../atoms/Utlis/BrandDatePicker';

interface InstallmentEditorProps {
  installments: InvoiceInstallment[];
  grandTotal: string;
  onChange: (installments: InvoiceInstallment[]) => void;
}

const emptyInstallment = (sequence: number): InvoiceInstallment => ({
  sequence,
  percent: '0',
  label: `Installment ${sequence}`,
  due_date: '',
  paid: false,
});

const SPLIT_40_30_30: InvoiceInstallment[] = [
  { sequence: 1, percent: '40', label: 'Advance', paid: false },
  { sequence: 2, percent: '30', label: 'Mid Payment', paid: false },
  { sequence: 3, percent: '30', label: 'Final Payment', paid: false },
];

const SPLIT_50_50: InvoiceInstallment[] = [
  { sequence: 1, percent: '50', label: 'Advance / Booking', paid: false },
  { sequence: 2, percent: '50', label: 'Final Payment', paid: false },
];

const SPLIT_100: InvoiceInstallment[] = [
  { sequence: 1, percent: '100', label: 'Full Payment', paid: false },
];

const InstallmentEditor: React.FC<InstallmentEditorProps> = ({
  installments,
  grandTotal,
  onChange,
}) => {
  const totalPercent = useMemo(
    () => installments.reduce((sum, i) => sum + parseFloat(i.percent || '0'), 0),
    [installments]
  );

  const isValid = Math.abs(totalPercent - 100) < 0.01;

  const handleAdd = () => {
    onChange([...installments, emptyInstallment(installments.length + 1)]);
  };

  const handleRemove = (idx: number) => {
    const updated = installments
      .filter((_, i) => i !== idx)
      .map((inst, i) => ({ ...inst, sequence: i + 1 }));
    onChange(updated);
  };

  const handleChange = (idx: number, field: keyof InvoiceInstallment, value: string | boolean) => {
    let sanitizedVal = value;
    if (field === 'percent') {
      const parsed = parseFloat(String(value));
      sanitizedVal = isNaN(parsed) ? '' : String(Math.max(0, parsed));
    }
    const updated = installments.map((inst, i) =>
      i === idx ? { ...inst, [field]: sanitizedVal } : inst
    );
    onChange(updated);
  };

  const computeAmount = (percent: string): string => {
    const total = parseFloat(grandTotal.replace(/,/g, '') || '0');
    const pct = parseFloat(percent || '0');
    if (isNaN(total) || isNaN(pct)) return '0.00';
    return formatINR((total * pct) / 100);
  };

  return (
    <Wrapper>
      <TopBar>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <LoadDefaultsBtn type="button" onClick={() => onChange(SPLIT_40_30_30)}>
            Load Default Split (40/30/30)
          </LoadDefaultsBtn>
          <LoadDefaultsBtn type="button" onClick={() => onChange(SPLIT_50_50)}>
            Load Default Split (50/50)
          </LoadDefaultsBtn>
          <LoadDefaultsBtn type="button" onClick={() => onChange(SPLIT_100)}>
            Load Default Split (100)
          </LoadDefaultsBtn>
        </div>
        <PercentBadge $valid={isValid}>
          Total: {totalPercent.toFixed(0)}% {!isValid && '⚠ Must equal 100%'}
        </PercentBadge>
      </TopBar>

      {!isValid && installments.length > 0 && (
        <ErrorBanner>
          <AlertCircle size={15} />
          Installment percentages must add up to exactly 100% before saving.
        </ErrorBanner>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Label</Th>
              <Th style={{ width: 90 }}>% Share</Th>
              <Th style={{ width: 140 }}>Amount (₹)</Th>
              <Th style={{ width: 160 }}>Due Date</Th>
              <Th style={{ width: 44 }}></Th>
            </tr>
          </thead>
          <tbody>
            {installments.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 20, color: 'rgba(23,23,23,0.42)', fontFamily: 'Montserrat,sans-serif', fontSize: 13 }}>
                  No installments. Load defaults or add manually.
                </td>
              </tr>
            ) : (
              installments.map((inst, idx) => (
                <tr key={idx}>
                  <Td style={{ color: 'rgba(23,23,23,0.45)', fontWeight: 700, fontSize: 13 }}>{inst.sequence}</Td>
                  <Td>
                    <Input
                      type="text"
                      value={inst.label}
                      onChange={e => handleChange(idx, 'label', e.target.value)}
                      placeholder="e.g. Advance"
                    />
                  </Td>
                  <Td>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={inst.percent}
                      onChange={e => handleChange(idx, 'percent', e.target.value)}
                      style={{ textAlign: 'center' }}
                    />
                  </Td>
                  <Td style={{ fontWeight: 700, color: '#171717', fontSize: 14 }}>
                    ₹{computeAmount(inst.percent)}
                  </Td>
                  <Td>
                    <BrandDatePicker
                      value={inst.due_date || ''}
                      onChange={(v) => handleChange(idx, 'due_date', v)}
                      size="compact"
                    />
                  </Td>
                  <Td>
                    <RemoveBtn type="button" onClick={() => handleRemove(idx)} aria-label="Remove installment">
                      <Trash2 size={14} />
                    </RemoveBtn>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>

      <AddRow>
        <AddBtn type="button" onClick={handleAdd}>
          <Plus size={15} />
          Add Installment
        </AddBtn>
      </AddRow>
    </Wrapper>
  );
};

export default InstallmentEditor;

// ─── Styled Components ────────────────────────────────────────

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const LoadDefaultsBtn = styled.button`
  padding: 7px 14px;
  border: 1.5px solid rgba(23, 23, 23, 0.15);
  border-radius: 8px;
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #c94daa;
    color: #c94daa;
  }
`;

const PercentBadge = styled.span<{ $valid: boolean }>`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 800;
  padding: 5px 12px;
  border-radius: 20px;
  background: ${({ $valid }) => ($valid ? 'rgba(49, 166, 106, 0.12)' : 'rgba(228, 72, 98, 0.10)')};
  color: ${({ $valid }) => ($valid ? '#31a66a' : '#e44862')};
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(228, 72, 98, 0.08);
  border: 1px solid rgba(228, 72, 98, 0.2);
  color: #e44862;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.08);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Urbanist, sans-serif;
`;

const Th = styled.th`
  padding: 10px 12px;
  background: rgba(23, 23, 23, 0.04);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(23, 23, 23, 0.55);
  text-align: left;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px 12px;
  border-top: 1px solid rgba(23, 23, 23, 0.05);
  vertical-align: middle;
`;

const Input = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 8px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: #171717;
  background: #fafafa;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #c94daa;
    background: #fff;
  }
`;

const RemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 7px;
  background: rgba(228, 72, 98, 0.1);
  color: #e44862;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(228, 72, 98, 0.2);
  }
`;

const AddRow = styled.div`
  display: flex;
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1.5px dashed rgba(201, 77, 170, 0.5);
  border-radius: 8px;
  background: rgba(201, 77, 170, 0.04);
  color: #c94daa;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #c94daa;
    background: rgba(201, 77, 170, 0.1);
  }
`;
